import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { GridRequest } from '../../models/gridRequest';
import { GridResponse } from '../../models/gridResponse';
import { AnyoComponent } from './AnyoComponent';

export abstract class AnyoGridComponent<
  SortKey extends string,
  FilterKey extends string,
  SearchKey extends string,
  DateKey extends string,
  G extends GridRequest<SortKey, FilterKey, SearchKey, DateKey>,
  FilterOptions,
  ItemType,
> extends AnyoComponent {
  constructor(
    protected override auth: AuthService,
    protected router: Router,
    protected route: ActivatedRoute,
  ) {
    super(auth);
  }

  // table state
  tableLoading = false;
  page = 1;
  pageSize = 10;
  totalRecords = 0;
  pageSizeOptions = [10, 20, 50, 100];
  data: ItemType[] = [];

  // hook: subclasses must fetch data
  protected abstract fetchGridData(
    request: G,
  ): Observable<GridResponse<FilterOptions | undefined, ItemType>>;

  // optionally overridden: build request from current state
  protected buildRequest(exportFlag = false): G {
    return {
      page: this.page,
      pageSize: this.pageSize,
      export: exportFlag,
    } as G;
  }

  // optionally overridden: apply request to local state
  protected applyRequestToState(req: G): void {
    if (typeof req.page === 'number') this.page = req.page;
    if (typeof req.pageSize === 'number') this.pageSize = req.pageSize;
  }

  async ready(): Promise<void> {
    const restored = this.restoreQueryFromUrl();
    if (restored) {
      this.applyRequestToState(restored);
    }
    this.fetch();
    return Promise.resolve();
  }

  fetch(): void {
    this.tableLoading = true;
    const req = this.buildRequest(false);
    this.updateUrlWithQuery(req);
    this.fetchGridData(req).subscribe({
      next: (res) => {
        this.data = res.data;
        this.totalRecords = res.totalRecords;
        this.pageSize = res.pageSize;
        this.tableLoading = false;
      },
      error: () => {
        this.tableLoading = false;
      },
    });
  }

  onPageIndexChange(p: number): void {
    this.page = p;
    this.fetch();
  }

  onPageSizeChange(s: number): void {
    this.pageSize = s;
    this.fetch();
  }

  // URL state sync helpers
  protected updateUrlWithQuery(req: G): void {
    const encoded = encodeURIComponent(JSON.stringify(req));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { gridQuery: encoded },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected restoreQueryFromUrl(): G | null {
    const qp = this.route.snapshot.queryParamMap.get('gridQuery');
    if (!qp) return null;
    try {
      const parsed = JSON.parse(decodeURIComponent(qp));
      return parsed as G;
    } catch {
      return null;
    }
  }
}
